import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { ChatActivity } from '@/shared/components/ChatActivity';

/** Translation keys for the first phase of microcopy (0–6s) */
const PHASE_ONE_TEXT_KEYS = [
  'rain.nonThinking.workingThroughIt',
  'rain.nonThinking.lookingIntoIt',
  'rain.nonThinking.sortingThingsOut',
  'rain.nonThinking.piecingItTogether',
] as const;

/** Translation keys for the second phase of microcopy (>6s) */
const PHASE_TWO_TEXT_KEYS = [
  'rain.nonThinking.connectingTheDots',
  'rain.nonThinking.zeroingIn',
  'rain.nonThinking.gettingToTheBottom',
  'rain.nonThinking.ironingOutTheDetails',
] as const;

/** Delay in ms before switching to the second random text */
const PHASE_TWO_DELAY_MS = 6000;

/**
 * Returns a random element from the provided array.
 *
 * @param {readonly T[]} items - Array to pick from
 * @returns {T} A randomly selected element
 */
function getRandomItem<T>(items: readonly T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

/**
 * Non-thinking loading indicator for QuillChat.
 *
 * Visually matches the ChatThinkingMessage streaming header from the design system:
 * italic shimmering text in a compact container. Unlike thinking messages, this has
 * no expandable content since there are no thinking steps to display.
 *
 * Text escalates through two phases to reassure the user during longer waits:
 * - Phase 1 (0–6s): One randomly selected shimmer text (e.g. "Working through it...").
 * - Phase 2 (>6s): Switches to a different set (e.g. "Connecting the dots...").
 *
 * The indicator disappears when the next backend state begins
 * or the response starts rendering, whichever comes first.
 *
 * @returns {JSX.Element} Shimmering status text matching the thinking message style
 */
export function NonThinkingSpinner() {
  const { t } = useTranslation('rain');

  /** Pre-select random texts on mount so they stay stable across re-renders */
  const phaseOneTextKeyRef = useRef(getRandomItem(PHASE_ONE_TEXT_KEYS));
  const phaseTwoTextKeyRef = useRef(getRandomItem(PHASE_TWO_TEXT_KEYS));

  const [displayTextKey, setDisplayTextKey] = useState<string>(
    phaseOneTextKeyRef.current,
  );

  // eslint-disable-next-line no-restricted-syntax -- reason: Timer-based text transition requires imperative setTimeout setup
  useEffect(() => {
    const phaseTwoTimer = setTimeout(() => {
      setDisplayTextKey(phaseTwoTextKeyRef.current);
    }, PHASE_TWO_DELAY_MS);

    return () => {
      clearTimeout(phaseTwoTimer);
    };
  }, []);

  return (
    <ChatActivity
      variant="thinking"
      title={t(displayTextKey)}
      isActive
      data-testid="non-thinking-spinner"
    />
  );
}
